import { createFileRoute } from "@tanstack/react-router";
import { APP_NAME } from "@/config/constants";

export const Route = createFileRoute("/_home/privacy-policy")({
  head: () => ({
    meta: [
      {
        title: `${APP_NAME} - Privacy Policy`,
      },
    ],
  }),
  component: PrivacyPolicy,
});

function PrivacyPolicy() {
  return (
    <div className="container mx-auto max-w-4xl px-4 py-8 pt-20">
      <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>

      <div className="prose prose-gray dark:prose-invert max-w-none">
        <p className="text-sm text-gray-600 mb-6">
          Last updated: {new Date().toLocaleDateString()}
        </p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
          <p>
            FilePrompt ("we," "our," or "us") is committed to protecting your privacy. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your information when you
            use our AI chat application and file management service.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>

          <h3 className="text-xl font-semibold mb-2">Personal Information</h3>
          <p>We may collect personal information that you provide directly to us, including:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Account information (email address, username)</li>
            <li>Profile information</li>
            <li>Communication preferences</li>
          </ul>

          <h3 className="text-xl font-semibold mb-2">File Data</h3>
          <p>
            We collect and store files that you upload to use with our AI chat service, including:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>File contents and metadata</li>
            <li>File organization and folder structures</li>
            <li>Chat interactions related to your files</li>
          </ul>

          <h3 className="text-xl font-semibold mb-2">Usage Information</h3>
          <p>We automatically collect information about how you use our service:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Chat history and AI interactions</li>
            <li>Feature usage patterns</li>
            <li>Technical information (IP address, browser type, device information)</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Provide and maintain our AI chat and file management services</li>
            <li>Process and respond to your AI chat requests</li>
            <li>Improve our service quality and user experience</li>
            <li>Communicate with you about service updates or issues</li>
            <li>Ensure the security and integrity of our platform</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">4. Data Storage and Security</h2>
          <p>
            Your data is stored on our secure servers. We implement appropriate technical and
            organizational measures to protect your personal information against unauthorized
            access, alteration, disclosure, or destruction.
          </p>
          <p>
            However, please note that no method of transmission over the internet or electronic
            storage is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">5. Your Data Rights</h2>
          <p>You have the following rights regarding your personal data:</p>

          <h3 className="text-xl font-semibold mb-2">Right to Delete</h3>
          <p>
            You have the right to request deletion of all your data at any time. When you request
            data deletion, we will permanently remove all your personal information, files, and chat
            history from our servers. This action cannot be undone.
          </p>

          <h3 className="text-xl font-semibold mb-2">Right to Access</h3>
          <p>You have the right to request access to the personal data we hold about you.</p>

          <h3 className="text-xl font-semibold mb-2">Right to Rectification</h3>
          <p>You have the right to request correction of inaccurate personal data.</p>

          <h3 className="text-xl font-semibold mb-2">Right to Portability</h3>
          <p>
            You have the right to request a copy of your data in a structured, machine-readable
            format.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">6. Data Sharing and Disclosure</h2>
          <p>
            We do not sell, trade, or otherwise transfer your personal information to third parties,
            except in the following circumstances:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>With your explicit consent</li>
            <li>To comply with legal obligations or court orders</li>
            <li>To protect our rights, privacy, safety, or property</li>
            <li>In connection with a business transfer or merger</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">7. AI Processing and Third-Party Services</h2>
          <p>
            Our AI chat functionality may process your files and conversations to provide
            intelligent responses. We may use third-party AI services to enhance our capabilities,
            but we ensure that any such services comply with our privacy standards.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">8. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to provide our services and
            fulfill the purposes outlined in this policy. You may request deletion of your data at
            any time, which will result in permanent removal from our systems.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">9. Children's Privacy</h2>
          <p>
            Our service is not intended for children under the age of 13. We do not knowingly
            collect personal information from children under 13. If we become aware that we have
            collected such information, we will delete it immediately.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own.
            We ensure that such transfers comply with applicable data protection laws and that your
            data receives adequate protection.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material
            changes by posting the new policy on this page and updating the "Last updated" date.
            Your continued use of the service after such changes constitutes acceptance of the
            updated policy.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or wish to exercise your data
            rights, please contact us through the appropriate channels provided in the application.
          </p>
        </section>
      </div>
    </div>
  );
}
